import { useMemo } from "react";

import { isCustomerPlatformBindingActive } from "@/helpers/customer-platform-binding.helpers";
import useGetCustomerPlatforms from "@/hooks/requests/customer-platform/use-get-customer-platforms";
import useGetAllPlatforms from "@/hooks/requests/platform/use-get-all-platform";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import { PlatformCategory } from "@/types/platform.types";

export type ActiveLogisticsPlatform = {
  platformId: number;
  name: string;
  displayName: string;
};

const useActiveLogisticsPlatforms = () => {
  const customerId = useGetCustomerId();
  const {
    data: customerPlatforms,
    isLoading: isLoadingCustomerPlatforms,
    error: customerPlatformsError,
  } = useGetCustomerPlatforms({
    customerId,
  });
  const {
    data: platformsPage,
    isLoading: isLoadingPlatforms,
    error: platformsError,
  } = useGetAllPlatforms({
    pageSize: 100,
  });

  const activeLogisticsPlatforms = useMemo((): ActiveLogisticsPlatform[] => {
    if (!customerPlatforms?.length || !platformsPage?.items?.length) return [];

    const platformById = new Map(platformsPage.items.map((platform) => [platform.id, platform]));
    const seen = new Set<number>();
    const options: ActiveLogisticsPlatform[] = [];

    for (const cp of customerPlatforms) {
      const platformId = Number(cp.platformId);
      if (!Number.isFinite(platformId) || seen.has(platformId)) continue;
      if (!isCustomerPlatformBindingActive(cp)) continue;

      const platform = platformById.get(platformId);
      if (!platform || Number(platform.type) !== PlatformCategory.logistics) continue;

      seen.add(platformId);
      options.push({
        platformId: platform.id,
        name: platform.name,
        displayName: platform.displayName || platform.name,
      });
    }

    return options.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [customerPlatforms, platformsPage?.items]);

  const activeLogisticsPlatformLabel = useMemo(() => {
    if (!activeLogisticsPlatforms.length) return null;
    return activeLogisticsPlatforms.map((platform) => platform.displayName).join(", ");
  }, [activeLogisticsPlatforms]);

  const isInitialLoading =
    (!customerPlatforms && isLoadingCustomerPlatforms) ||
    (!platformsPage && isLoadingPlatforms);

  return {
    activeLogisticsPlatforms,
    activeLogisticsPlatformLabel,
    isInitialLoading,
    hasLoadError: Boolean(customerPlatformsError || platformsError),
  };
};

export default useActiveLogisticsPlatforms;
